import type { TFunction } from 'i18next'
import { z } from 'zod'

import { testIsValidName } from '@/requests/signup'
import { getDobFieldErrors } from '@/utils/dob'
import { ADDRESS_DEFAULTS, addressSchema, refineAddress } from './address'

const trimmed = z.string().trim()

export const personalInfoSchema = addressSchema.extend({
  firstName: trimmed,
  lastName: trimmed,
  dob: trimmed,
})

export type PersonalInfoValues = z.infer<typeof personalInfoSchema>

export const PERSONAL_INFO_DEFAULTS: PersonalInfoValues = {
  ...ADDRESS_DEFAULTS,
  firstName: '',
  lastName: '',
  dob: '',
}

const refineName = (
  value: string,
  path: 'firstName' | 'lastName',
  message: string,
  ctx: z.RefinementCtx
): void => {
  if (value.length < 2 || !testIsValidName(value)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message })
  }
}

const refineDob = (dob: string, ctx: z.RefinementCtx, t: TFunction): void => {
  const fail = (message: string) =>
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['dob'], message })

  if (!dob) return fail(t('personalInfo.error.birthdayRequired'))

  const { year, month, day, date } = getDobFieldErrors(dob)
  const errorKey = year ?? month ?? day ?? date
  if (errorKey) return fail(t(errorKey))
}

export const buildPersonalInfoSchema = (t: TFunction) =>
  personalInfoSchema.superRefine((values, ctx) => {
    refineName(
      values.firstName,
      'firstName',
      t('personalInfo.error.firstNameRequired'),
      ctx
    )
    refineName(
      values.lastName,
      'lastName',
      t('personalInfo.error.lastNameRequired'),
      ctx
    )
    refineDob(values.dob, ctx, t)
    refineAddress(values, ctx, t)
  })
